import { GroupUserRow, SharedEmailAddressRow, SharedEmailUserRow, UserSearchRow } from "@/data/models/SharedUsers";
import { userStatus, UserStatusOption } from "@/utils/ClientUserList";

/**
 * The lists of the IRES Admin Client's Benutzer suchen and Geteilte Benutzer (2026-11-03). Pure, so the
 * screens only bind.
 */

/** The shortest search IRES Cloud answers (`MIN_SEARCH_LENGTH` there). */
export const MIN_USER_SEARCH_LENGTH = 3;

/** Every status ticked: the search shows deleted users too, dimmed (Taimoor, 2026-11-03). */
export const DEFAULT_USER_SEARCH_STATUSES: UserStatusOption[] = [
  UserStatusOption.ACTIVE,
  UserStatusOption.BLOCKED,
  UserStatusOption.DELETED,
];

/** The search as IRES Cloud gets it, or null while it is too short to send. */
export function userSearchQuery(input: string | null | undefined): string | null {
  const query = (input ?? "").trim();
  return query.length >= MIN_USER_SEARCH_LENGTH ? query : null;
}

function compareText(a: string | null, b: string | null): number {
  // Missing values last, as the Benutzer list sorts.
  if (!a || !b) {
    return a ? -1 : b ? 1 : 0;
  }
  return a.localeCompare(b, "de", { sensitivity: "base" });
}

/**
 * The found users with a ticked status, live before deleted, then by last name, first name and client.
 *
 * @param rows - IRES Cloud's rows.
 * @param statuses - the ticked statuses; none ticked shows nothing.
 */
export function selectUserSearchRows(rows: UserSearchRow[], statuses: UserStatusOption[]): UserSearchRow[] {
  return rows
    .filter((row) => statuses.includes(userStatus(row)))
    .sort(
      (a, b) =>
        Number(a.deleted) - Number(b.deleted) ||
        compareText(a.lastName, b.lastName) ||
        compareText(a.firstName, b.firstName) ||
        compareText(a.clientName, b.clientName)
    );
}

/**
 * Which "E-Mail-Adresse teilen" entries show: every one, those to clean up, the healthy ones, or those
 * left behind by "Geteilte Benutzer migrieren".
 */
export type SharedEmailAddressFilter = "ALL" | "CLEAN_UP" | "OK" | "MIGRATED";

export const SHARED_EMAIL_ADDRESS_FILTERS: SharedEmailAddressFilter[] = ["ALL", "CLEAN_UP", "OK", "MIGRATED"];

function matchesSharedEmailAddressFilter(entry: SharedEmailAddressRow, filter: SharedEmailAddressFilter): boolean {
  switch (filter) {
    case "CLEAN_UP":
      return entry.state !== "OK";
    case "OK":
      return entry.state === "OK";
    case "MIGRATED":
      return entry.migrated;
    default:
      return true;
  }
}

/** The users Bereinigen takes out: everyone who no longer carries the address. */
export function usersToCleanUp(entry: SharedEmailAddressRow): SharedEmailUserRow[] {
  return entry.users.filter((user) => user.state !== "LIVE");
}

function includesText(values: (string | null)[], search: string): boolean {
  return values.some((value) => !!value && value.toLowerCase().includes(search));
}

/**
 * The entries a filter and a search show: those with nobody left first, then those with some users
 * gone, then the healthy ones, each by address. The search matches the address, a user's name or
 * nickname, or a client's name.
 *
 * @param entries - IRES Cloud's entries.
 * @param filter - the filter.
 * @param search - the search, any case.
 */
export function selectSharedEmailAddresses(
  entries: SharedEmailAddressRow[],
  filter: SharedEmailAddressFilter,
  search: string
): SharedEmailAddressRow[] {
  const text = search.trim().toLowerCase();
  const order = { EMPTY: 0, STALE: 1, OK: 2 };
  return entries
    .filter((entry) => matchesSharedEmailAddressFilter(entry, filter))
    .filter(
      (entry) =>
        text === "" ||
        includesText([entry.emailAddress, entry.sourceClientName ?? null], text) ||
        entry.users.some((user) => includesText([user.name, user.nickname, user.clientName], text))
    )
    .sort((a, b) => order[a.state] - order[b.state] || compareText(a.emailAddress, b.emailAddress));
}

/**
 * The group users a search shows, live before deleted, then by group and name. The search matches the
 * name, nickname, address or the group's name.
 *
 * @param users - IRES Cloud's group users.
 * @param search - the search, any case.
 */
export function selectGroupUsers(users: GroupUserRow[], search: string): GroupUserRow[] {
  const text = search.trim().toLowerCase();
  return users
    .filter(
      (user) =>
        text === "" ||
        includesText(
          [user.firstName, user.lastName, user.nickname, user.emailAddress, user.administratorClientName, user.homeClientName ?? null],
          text
        ) ||
        includesText([`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()], text)
    )
    .sort(
      (a, b) =>
        Number(a.deleted) - Number(b.deleted) ||
        compareText(a.administratorClientName, b.administratorClientName) ||
        compareText(a.lastName, b.lastName) ||
        compareText(a.firstName, b.firstName)
    );
}
